import { createStyles, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import * as React from "react";

import {
    AreaNotificationMessage,
    baseColors,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Input,
    RelodeTheme,
    telemetryService,
    Text,
    useDeviceSize,
} from "../../..";
import { CommentIcon, EnvelopeIcon } from "../../../assets/icons";

type MarketResearchShareDialogProps = {
    jobId?: number;
    jobName: string;
    locationName: string;
    shortenedUrl: string;
    emailUrl: string;
    open?: boolean;
    onClose: () => void;
};

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            infoText: {
                paddingBottom: theme.spacing(2),
                color: theme.palette.grey[500],
            },
            input: {
                margin: theme.spacing(0, 2, 0, 0),
                flex: 1,
            },
            urlShare: {
                display: "flex",
                margin: theme.spacing(2, 0),
            },
            emailShare: {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
            },
            shareActions: {
                display: "flex",
                marginBottom: theme.spacing(2),
            },
            shareButton: {
                background: theme.relode.colors.socialShare.email.main,
                border: "1px solid #eee",
                color: baseColors.navy,
                marginBottom: theme.spacing(2),
                marginRight: theme.spacing(2),
                "&:hover": {
                    color: baseColors.navy,
                    background: theme.relode.colors.socialShare.email.light,
                },
            },
            buttonCopy: {},
            icon: {
                marginRight: theme.spacing(1),
            },
            [theme.breakpoints.down("sm")]: {
                urlShare: {
                    flexDirection: "column",
                },
                shareButton: {
                    width: "100%",
                    marginRight: 0,
                },
                buttonCopy: {
                    marginTop: theme.spacing(2),
                },
                infoText: {
                    textAlign: "center",
                },
                shareButtonsContainer: {
                    display: "block",
                    width: "100%",
                },
            },
        }),
    { name: "MarketResearchShareDialog" }
);

export const MarketResearchShareDialog = (
    props: MarketResearchShareDialogProps
) => {
    const classes = useStyles(props);
    const {
        open,
        jobId,
        onClose,
        jobName,
        locationName,
        emailUrl,
        shortenedUrl,
    } = props;

    const device = useDeviceSize();
    const [textCopied, setTextCopied] = React.useState(false);
    const fileInput = React.useRef<HTMLInputElement>();

    const handleCloseDialog = () => {
        onClose();
    };

    const handleCopy = () => {
        telemetryService.trackEvent("MarketResearch-configured-shared-copy", {
            jobId,
        });
        fileInput.current && fileInput.current.select();
        document.execCommand("copy");
        setTextCopied(true);
    };

    const handleEmailClick = () => {
        telemetryService.trackEvent("MarketResearch-configured-shared-email", {
            jobId,
        });
    };

    const handleSmsClick = () => {
        telemetryService.trackEvent("MarketResearch-configured-shared-sms", {
            jobId,
        });
    };

    const iOS =
        !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);

    const smsUrl = iOS
        ? `sms:&body=${encodeURIComponent(
              `Data to share for job position ${jobName}. ${shortenedUrl}`
          )}`
        : `sms:?body=${encodeURIComponent(
              `Data to share for job position ${jobName}. ${shortenedUrl}`
          )}`;

    return (
        <Dialog open={open}>
            <DialogTitle
                title="share market research"
                onClose={handleCloseDialog}
            />
            <DialogContent>
                <Text variant="h4">{`${jobName} in ${locationName}`}</Text>
                <br />
                <AreaNotificationMessage level="warning">
                    This market research contains sensitive data including job
                    salary. Please exercise caution when sharing this link.
                </AreaNotificationMessage>
                {!!shortenedUrl ? (
                    <>
                        <div className={classes.urlShare}>
                            <Input
                                autoFocus
                                value={shortenedUrl}
                                inputRef={fileInput}
                                className={classes.input}
                            />
                            <Button
                                data-cy="copyButton"
                                size="small"
                                color="primary"
                                variant="contained"
                                onClick={handleCopy}
                                className={classes.buttonCopy}
                                fullWidth={device === "mobile"}
                            >
                                {textCopied ? "copied" : "copy"}
                            </Button>
                        </div>

                        <div className={classes.emailShare}>
                            <p className={classes.infoText}>
                                share via email or text
                            </p>
                            <div className={classes.shareButtonsContainer}>
                                <Button
                                    onClick={handleEmailClick}
                                    variant="contained"
                                    href={emailUrl}
                                    color="default"
                                    size="small"
                                    fullWidth={device === "mobile"}
                                    className={classes.shareButton}
                                    target="_blank"
                                >
                                    <EnvelopeIcon className={classes.icon} />{" "}
                                    email
                                </Button>
                                <Button
                                    onClick={handleSmsClick}
                                    variant="contained"
                                    href={smsUrl}
                                    color="default"
                                    size="small"
                                    fullWidth={device === "mobile"}
                                    className={classes.shareButton}
                                    target="_blank"
                                >
                                    <CommentIcon className={classes.icon} />{" "}
                                    text
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <Skeleton
                            variant="rect"
                            height={40}
                            style={{ marginBottom: 16, marginTop: 16 }}
                        />
                        <Skeleton
                            variant="rect"
                            height={36}
                            style={{ marginBottom: 8, marginTop: 8 }}
                        />
                        <Skeleton
                            variant="rect"
                            height={56}
                            style={{ marginBottom: 16 }}
                        />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
